/**
 * Pure data + Mapbox config types split out from `./layers.ts`.
 *
 * Side-effect free (no valtio/lodash/prefs/TOCLayers) so the static portion of
 * the store can be imported without the Valtio runtime, and acts as the client
 * mirror of the per-layer config the DB feeds at read time (`tileLayers`
 * is a raw 1:1 GIS mirror; normalized config is derived in `@civilgrid/utils`, not
 * a DB column). `layers.ts` re-exports everything below for back-compat.
 *
 * `DEFAULT_METADATA_IDS` below is client-owned and has no DB source — consumers read it
 * unconditionally.
 */

// ---------------------------------------------------------------------------
// Mapbox source config types
// ---------------------------------------------------------------------------

interface Legend {
  label: string;
  imageData: string;
  height: number;
  width: number;
}

type Fields = {
  [key: string]: {
    alias: string;
    type: 'id' | 'string' | 'date';
  };
};

export interface VectorLayer {
  id: string;
  type: 'line' | 'symbol' | 'fill' | 'circle';
  source: string;
  // One of `['==', field, value]` per ESRI category, `['in', field, ...values]` for a collapsed
  // renderer, or `['all', ['!=', field, value], …]` for a defaultSymbol catch-all.
  filter?: any[];
  layout: { [key: string]: any };
  paint: {
    [key: string]: any;
  };
}

export interface MapboxVectorSourceConfig {
  id: string;
  name: string;
  type: 'vector';
  tiles: [string];
  minZoom: number;
  maxZoom: number;
  url: string;
  layers: VectorLayer[];
  fields: Fields;
  legend: Legend[];
}

export interface MapboxRasterSourceConfig {
  id: string;
  subId: number;
  name: string;
  type: 'raster';
  tiles: [string];
  minZoom: number;
  maxZoom: number;
  url: string;
  fields: Fields;
  legend: Legend[];
}

export type MapboxSourceConfig = MapboxVectorSourceConfig | MapboxRasterSourceConfig;

export type TilesConfig = { [key: string]: MapboxSourceConfig };

export interface Layer {
  id: string;
  visible: boolean;
}

export type { Fields, Legend };

export const DEFAULT_METADATA_IDS = [
  'electricDistributionLines.voltage_diameter',
  'electricTransmissionLines.voltage_diameter',
  'gasDistribution.material',
  'gasDistribution.diameter',
  'gasTransmission.material',
  'gasTransmission.diameter',
  'sanitaryPipes.material',
  'sanitaryPipes.diameter',
  'waterMain.material',
  'waterMain.diameter',
  'waterTransmission.material',
  'waterTransmission.diameter',
  'recycledWaterPipes.material',
  'recycledWaterPipes.diameter',
  'waterService.material',
  'waterService.diameter',
  'stormPipes.material',
  'stormPipes.diameter',
  'fiberRoutes.CARRIER',
  'landUsePostmiles.PMc',
] as const;
